import React from "react";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { Button } from "@/components/ui/button";
import { useDashboardChartConfigPage } from "@/modules/administration/features/StochasticDashboardChartConfig/hooks/useDashboardChartConfigPage";

export default function DashboardChartConfigPage() {
  const {
    stages,
    selectedStage,
    selectStage,
    includedKeys,
    availableCharts,
    labelFor,
    addChart,
    removeChart,
    moveChart,
    save,
    resetToDefaults,
    isDirty,
    loading,
    saving,
  } = useDashboardChartConfigPage();

  return (
    <MainPageWrapper
      actions={
        <div className="flex items-center gap-3">
          {isDirty && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Unsaved changes
            </span>
          )}
          <Button
            disabled={saving || !selectedStage?.isCustomized}
            onClick={resetToDefaults}
            type="button"
            variant="outline"
          >
            Reset to Defaults
          </Button>
          <Button disabled={!isDirty || saving} onClick={save} type="button">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      }
      subtitle="Which Sales and Customer Dashboard charts each stage sees."
      title="Dashboard Charts"
    >
      {loading ? (
        <LoadingIndicator />
      ) : (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            {stages.map((stage) => (
              <button
                className={
                  stage.uuid === selectedStage?.uuid
                    ? "px-3 py-1.5 text-sm rounded-md bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white"
                    : "px-3 py-1.5 text-sm rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                }
                key={stage.uuid}
                onClick={() => selectStage(stage.uuid)}
                type="button"
              >
                {stage.name}
              </button>
            ))}
          </div>

          {selectedStage && (
            <>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {selectedStage.isCustomized
                  ? "This stage has a saved configuration."
                  : "This stage is using the built-in defaults."}
              </p>

              <div className="grid gap-6 lg:grid-cols-2">
                <section className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg">
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700">
                    <h2 className="text-base font-normal text-gray-900 dark:text-white">
                      Included charts
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Shown to {selectedStage.name} members, top to bottom.
                    </p>
                  </div>
                  <ul className="divide-y divide-gray-100 dark:divide-slate-700/50">
                    {includedKeys.map((key, index) => (
                      <li
                        className="flex items-center justify-between px-4 py-2.5"
                        key={key}
                      >
                        <span className="text-sm text-gray-900 dark:text-white">
                          {labelFor(key)}
                        </span>
                        <span className="flex items-center gap-1">
                          <button
                            aria-label={`Move ${labelFor(key)} up`}
                            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-30"
                            disabled={index === 0}
                            onClick={() => moveChart(key, -1)}
                            type="button"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button
                            aria-label={`Move ${labelFor(key)} down`}
                            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-30"
                            disabled={index === includedKeys.length - 1}
                            onClick={() => moveChart(key, 1)}
                            type="button"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </button>
                          <button
                            aria-label={`Remove ${labelFor(key)}`}
                            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                            onClick={() => removeChart(key)}
                            type="button"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </span>
                      </li>
                    ))}
                    {includedKeys.length === 0 && (
                      <li className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">
                        No charts selected — members at this stage would see an
                        empty dashboard. Add charts from the right.
                      </li>
                    )}
                  </ul>
                </section>

                <section className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg">
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700">
                    <h2 className="text-base font-normal text-gray-900 dark:text-white">
                      Available charts
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Not shown to this stage. Add to append at the bottom.
                    </p>
                  </div>
                  <ul className="divide-y divide-gray-100 dark:divide-slate-700/50">
                    {availableCharts.map((item) => (
                      <li
                        className="flex items-center justify-between px-4 py-2.5"
                        key={item.key}
                      >
                        <span className="text-sm text-gray-900 dark:text-white">
                          {item.label}
                        </span>
                        <button
                          aria-label={`Add ${item.label}`}
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                          onClick={() => addChart(item.key)}
                          type="button"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                    {availableCharts.length === 0 && (
                      <li className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">
                        Every chart is included for this stage.
                      </li>
                    )}
                  </ul>
                </section>
              </div>
            </>
          )}
        </div>
      )}
    </MainPageWrapper>
  );
}
